import { request } from './index'
import { HTTP_METHODS } from './const'

class Cart {
  static ADD_CART = 'AddToCart'

  static UPDATE_CART = 'UpdateCart'

  static DELETE_FROM_CART = 'DeleteFromCart'

  static GET_CART = 'GET'

  static USE_VOUCHER = 'USE_VOUCHER'

  static REMOVE_VOUCHER = 'REMOVE_VOUCHER'

  static ADD_TO_CART_FROM_WISHLIST = 'ADD_TO_CART_FROM_WISHLIST'

  static SELECT_ITEM = 'SELECT_ITEM'

  static UNSELECT_ITEM = 'UNSELECT_ITEM'

  static get(options) {
    switch (options.type) {
      case Cart.GET_CART:
        return Cart.getCart(options)
      case Cart.ADD_CART:
        return Cart.addToCart(options)
      case Cart.UPDATE_CART:
        return Cart.updateCart(options)
      case Cart.DELETE_FROM_CART:
        return Cart.deleteFromCart(options)
      case Cart.USE_VOUCHER:
      case Cart.REMOVE_VOUCHER:
        return Cart.handleVoucherCode(options)
      case Cart.ADD_TO_CART_FROM_WISHLIST:
        return Cart.addToCartFromWishlist(options)
      case Cart.SELECT_ITEM:
      case Cart.UNSELECT_ITEM:
        return Cart.setItemSelected(options)
      default:
        return Cart.getCart(options)
    }
  }

  // current/view's own items[] silently filters out unselected lines --
  // confirmed live: deselecting a cart's only item drops current/view's
  // items to [] even though the item is still in the cart, just unchecked.
  // Its add/remove path (v6/products/.../add-to-cart, PATCH
  // v6/carts/current) is a different write path than selectable-cart's own
  // add-product/remove-product/select-product/unselect-product actions,
  // and mixing the two was the actual source of the stale-after-delete
  // items seen earlier: a delete made via PATCH current wasn't visible yet
  // to a same-moment selectable-cart view-product call, which kept
  // surfacing the deleted (and sometimes duplicated) line. Every mutation
  // below now goes through selectable-cart's own actions instead, so its
  // own item list -- and the is_selected per line, which current/view
  // doesn't even expose -- stays consistent with what it just did.
  // current/view has no analogue for that, but it's still the only source
  // for summary/eligibility/metadata (selectable-cart's response is
  // items-only), so it's merged in for those fields alone, never for
  // items.
  static viewSelectableCart() {
    const config = {
      method: HTTP_METHODS.post,
      url: 'v6/carts/selectable-cart',
      body: {
        actions: [{ type: 'view-product' }],
      },
    }

    return request(config).catch((err) => ({ ...err, ok: false }))
  }

  // options.skipSelectableCart reads current/view on its own, without the
  // parallel selectable-cart view-product call above. Only checkout passes
  // it (see CheckoutRedesign/pages/checkout). The bag/drawer keeps the
  // two-call merge, which it needs: that's where the per-item checkboxes
  // live, and the is_selected they render only ever comes from
  // selectable-cart.
  //
  // Checkout doesn't render those checkboxes at all -- CartItems is given
  // `fromSummary` there (components/checkout/summary/cart-item-list), and
  // CartProduct gates the checkbox on !fromSummary -- so is_selected is
  // unused on that page. current/view's own filtering is what checkout
  // wants besides: it returns only is_selected lines, which is exactly the
  // set being paid for, and its summary/eligibility/metadata already
  // describe that same set. Taking selectable-cart's items there instead
  // put unselected lines back into the order review while the totals
  // beside them still excluded those lines.
  static getCart(options) {
    const params = {
      'include[id_cart]': true,
    }

    const config = {
      method: HTTP_METHODS.post,
      url: `v6/carts/current/view`,
      body: options.body,
      params,
    }

    const currentView = request(config).catch((err) => ({ ...err, ok: false }))

    if (options.skipSelectableCart) {
      return currentView
    }

    return Promise.all([currentView, Cart.viewSelectableCart()]).then(
      ([cart, selectableCart]) => {
        if (cart?.ok === false || cart?.error) {
          return cart
        }
        if (!Array.isArray(selectableCart?.items)) {
          return cart
        }
        return { ...cart, items: selectableCart.items }
      },
    )
  }

  // Runs a single selectable-cart action and folds the response's own
  // items into the next getCart call (see getCart's own comment) instead
  // of going through v6/products/.../add-to-cart or PATCH v6/carts/current.
  static mutateSelectableCart(action) {
    const config = {
      method: HTTP_METHODS.post,
      url: 'v6/carts/selectable-cart',
      body: { actions: [action] },
    }

    return request(config).catch((err) => ({ ...err, ok: false }))
  }

  static addToCart(options) {
    const action = {
      type: 'add-product',
      id_product: parseInt(options.productID, 10),
      id_product_attribute: parseInt(options.productAttributeID, 10),
      quantity: options.quantity,
    }

    return Cart.mutateSelectableCart(action)
      .then(() => Cart.getCart(options))
      .catch((err) => ({ ...err, ok: false }))
  }

  static updateCart(options) {
    const deleteOptions = {
      ...options,
      productAttributeID: options.productToDeleteAttributeID,
      skipCartRender: true,
    }
    const addOptions = {
      ...options,
      productAttributeID: options.productToAddAttributeID,
      skipCartRender: options.skipCartRender,
    }

    return Cart.deleteFromCart(deleteOptions)
      .then(() => Cart.addToCart(addOptions))
      .catch((err) => ({ ...err, ok: false }))
  }

  static deleteFromCart(options) {
    const action = {
      type: 'remove-product',
      id_product: parseInt(options.productID, 10),
      id_product_attribute: parseInt(options.productAttributeID, 10),
    }

    if (options.skipCartRender) {
      return Cart.mutateSelectableCart(action)
    }

    return Cart.mutateSelectableCart(action)
      .then(() => Cart.getCart(options))
      .catch((err) => ({ ...err, ok: false }))
  }

  static handleVoucherCode(options) {
    const config = {
      method: HTTP_METHODS.post,
      url: 'v6/carts/current/apply',
      body: {
        voucher_code: options.voucherCode,
      },
    }

    return request(config).catch(
      (err) => err.error?.message || err.response?.data?.error?.message,
    )
  }

  // POST /v6/carts/selectable-cart's select-product/unselect-product
  // action. The caller (cartDuck.creators.setItemSelected, duck.js)
  // follows this with its own Cart.getCart refetch for the fresh
  // summary/total, so unselecting the last-selected line correctly drops
  // it from the refreshed item list too -- current/view only ever
  // returns is_selected items.
  static setItemSelected(options) {
    const config = {
      method: HTTP_METHODS.post,
      url: 'v6/carts/selectable-cart',
      body: {
        actions: [
          {
            type:
              options.type === Cart.SELECT_ITEM
                ? 'select-product'
                : 'unselect-product',
            id_product: parseInt(options.productID, 10),
            id_product_attribute: parseInt(options.productAttributeID, 10),
          },
        ],
      },
    }

    return request(config).catch((err) => ({ ...err, ok: false }))
  }

  // "Select all"/"deselect all" used to call setItemSelected once per
  // affected item (CartSelectAllHeader's own forEach) -- each one its own
  // selectable-cart POST plus its own Cart.getCart refetch. Confirmed
  // live: toggling a 17-item cart fired dozens of overlapping requests in
  // a few seconds, some even rejected with 400s from the pileup. This
  // sends every item's select-product/unselect-product action in the
  // same request instead, per the API spec's own actions: [...] array.
  static setItemsSelected(actions) {
    const config = {
      method: HTTP_METHODS.post,
      url: 'v6/carts/selectable-cart',
      body: {
        actions: actions.map(({ type, productID, productAttributeID }) => ({
          type:
            type === Cart.SELECT_ITEM ? 'select-product' : 'unselect-product',
          id_product: parseInt(productID, 10),
          id_product_attribute: parseInt(productAttributeID, 10),
        })),
      },
    }

    return request(config).catch((err) => ({ ...err, ok: false }))
  }

  static addToCartFromWishlist(options) {
    const { body, wishlistID } = options
    const config = {
      method: HTTP_METHODS.post,
      url: `v6/wishlists/${wishlistID}/add-to-cart`,
      body,
    }

    return request(config).catch((err) => err)
  }
}

export default Cart
